import { FC, useCallback, useContext, useEffect, useState } from 'react'

import { Box, Button, HStack, Icon, IconButton, Input, Menu, Portal, Spacer, Text, VStack } from '@chakra-ui/react'
import { AddIcon, AutoAwesomeIcon, CancelIcon, ResetIcon } from '@editor/assets/icons'
import { DOCUMENT_SIZES, DocumentSize, MODAL_TYPES } from '@editor/config/constants'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { usePrintScale } from '@editor/hooks/usePrintScale'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { GRID_OPACITY } from '@editor/pages/projects/blueprint-editor/objects/CanvasGrid/constants'
import {
  setGridOpacity,
  setSelectedTool,
  updateBlueprint,
} from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { resetWorking } from '@editor/pages/projects/editor/tools/MaskPCD/store'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { withPromiseContained } from '@editor/services/Util'
import { GenericError } from '@editor/services/errors/GenericError'
import { RootState, useAppDispatch } from '@editor/store/app'
import { RangeInput } from '@infraloop/common'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

const BlueprintSettings: FC = () => {
  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)

  const isAllowedToModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const gridOpacity = useSelector((state: RootState) => state.blueprint.editor.gridOpacity)
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showModal, showErrorModal } = useGlobalModalContext()

  // Print Scale hook
  const { currentScaleString, canModify, updatePrintScaleFromString, calculateScaleForDocumentSize, scaleToString } =
    usePrintScale()

  // Local state for print scale input
  const [scaleInputValue, setScaleInputValue] = useState(currentScaleString)

  // Vars
  const hasMaskRegion = !!selectedBlueprint?.masking_region
  const isModifyingMaskRegion = [BlueprintEditorTools.MaskPCD, BlueprintEditorTools.Focus].includes(selectedTool) // Focus is only available during PCD masking

  // Update local state when current scale changes
  useEffect(() => {
    setScaleInputValue(currentScaleString)
  }, [currentScaleString])

  /**
   * Handle resetting blueprint's mask region.
   */
  const handleReset = useCallback(() => {
    if (!project || !inspectionArea || !selectedBlueprint) {
      return
    }

    showModal({
      title: '注目領域',
      body: <Text>一度削除してしまうと、元に戻せません。</Text>,
      confirmText: '削除',
      modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
      onConfirmPromised: async () => {
        await withPromiseContained(async () => {
          const token = await getAccessToken()
          if (!token) return

          // TODO: progress flag for mask region reset

          const result = await dispatch(
            updateBlueprint({
              accessToken: token,
              projectId: project.project_id,
              areaId: inspectionArea.inspection_area_id,
              blueprint: {
                ...selectedBlueprint,
                masking_region: null,
              },
              updateMaskingRegion: true,
            }),
          )
          if (!result) return

          mixpanel.track('Deleted mask region')
        }).catch((err) => {
          showErrorModal(err instanceof GenericError ? err.message : COMMON_MESSAGE)
        })
        return true
      },
    })
  }, [project, inspectionArea, selectedBlueprint, getAccessToken, dispatch, showModal, showErrorModal])

  /**
   * Handle print scale input changes.
   */
  const handleScaleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setScaleInputValue(value)
  }, [])

  /**
   * Handle print scale input blur (when user finishes editing).
   */
  const handleScaleInputBlur = useCallback(async () => {
    if (!canModify || scaleInputValue === currentScaleString) return

    const success = await updatePrintScaleFromString(scaleInputValue)
    if (success) {
      mixpanel.track('Blueprint Print Scale Updated', {
        input_method: 'manual input',
        blueprint_id: selectedBlueprint?.blueprint_id,
        scale: scaleInputValue,
      })
    } else {
      // Reset to current value if update failed
      setScaleInputValue(currentScaleString)
      showErrorModal('スケールの更新に失敗しました。')
    }
  }, [canModify, scaleInputValue, currentScaleString, updatePrintScaleFromString, selectedBlueprint, showErrorModal])

  /**
   * Handle document size selection.
   */
  const handleDocumentSizeSelect = useCallback(
    async (size: DocumentSize) => {
      const calculatedScale = calculateScaleForDocumentSize(size)
      const scaleString = scaleToString(calculatedScale)
      setScaleInputValue(scaleString)

      const success = await updatePrintScaleFromString(scaleString)
      if (success) {
        mixpanel.track('Blueprint Print Scale Updated', {
          input_method: 'document size',
          blueprint_id: selectedBlueprint?.blueprint_id,
          document_size: size,
          scale: scaleString,
        })
      } else {
        showErrorModal('スケールの更新に失敗しました。')
      }
    },
    [calculateScaleForDocumentSize, scaleToString, updatePrintScaleFromString, selectedBlueprint, showErrorModal],
  )

  /**
   * Handle grid opacity change.
   */
  const handleGridOpacityChange = useCallback(
    (value: number) => {
      dispatch(setGridOpacity(value / 100)) // Convert from 0-100 to 0-1
    },
    [dispatch],
  )

  if (!selectedBlueprint) return null

  return (
    <VStack alignItems="flex-start" p={2} px={4} w="100%" gap={4}>
      <VStack w="100%" alignItems="flex-start" gap={3}>
        <Text fontSize="sm" fontWeight="bold">
          設計図面名
        </Text>
        <Box
          bgColor="gray2.700"
          borderWidth={1}
          borderColor="whiteAlpha.100"
          w="100%"
          p={2}
          borderRadius="md"
          minH="32px"
          display="flex"
          alignItems="center"
        >
          {selectedBlueprint.name}
        </Box>
      </VStack>

      {!isCreatingBlueprint && (
        <HStack w="100%">
          <Text fontSize="sm" fontWeight="bold">
            注目領域
          </Text>
          <Spacer />

          {!hasMaskRegion && !isModifyingMaskRegion && isAllowedToModify ? (
            <Button
              // TODO:variant="panel-dark-outline"
              size="xs"
              fontWeight="normal"
              onClick={() => {
                dispatch(setSelectedTool(BlueprintEditorTools.MaskPCD))
              }}
            >
              <AddIcon />
              設定
            </Button>
          ) : null}

          {isModifyingMaskRegion && isAllowedToModify ? (
            <Button
              variant="panel-dark-outline"
              borderColor="red.500"
              size="xs"
              fontWeight="normal"
              onClick={() => {
                // TOOD: Add a confirmation modal before resetting

                dispatch(setSelectedTool(BlueprintEditorTools.Selector))
                dispatch(setAttentionText({ message: '' }))
                dispatch(resetWorking())
              }}
            >
              <CancelIcon color="{colors.red.500}" />
              キャンセル
            </Button>
          ) : null}

          {hasMaskRegion && !isModifyingMaskRegion && isAllowedToModify ? (
            <Button variant="panel-dark-outline" size="xs" fontWeight="normal" onClick={handleReset}>
              <ResetIcon />
              リセット
            </Button>
          ) : null}
        </HStack>
      )}

      {/* Print Scale Section */}
      {!isModifyingMaskRegion && (
        <HStack w="100%" alignItems="center" gap={0}>
          <Text fontSize="sm" fontWeight="bold" minW="fit-content">
            印刷スケール
          </Text>
          <Spacer />
          <HStack gap={1}>
            <Text fontSize="sm" minW="fit-content">
              1:
            </Text>
            <Input
              size="xs"
              w="64px"
              value={scaleInputValue}
              onChange={handleScaleInputChange}
              onBlur={handleScaleInputBlur}
              placeholder="100"
              type="number"
              min="1"
              max="10000"
              disabled={!canModify}
              bgColor="gray2.700"
              borderColor="whiteAlpha.300"
              _focus={{
                borderColor: 'blue.400',
                bgColor: 'gray2.600',
              }}
              _disabled={{
                opacity: 0.6,
                cursor: 'not-allowed',
              }}
            />
          </HStack>
          <Menu.Root positioning={{ strategy: 'fixed' }}>
            <Menu.Trigger asChild>
              <IconButton
                aria-label="用紙サイズから自動計算"
                size="xs"
                variant="ghost"
                colorPalette="gray2.400"
                disabled={!canModify}
                title="用紙サイズから自動計算"
              >
                <Icon as={AutoAwesomeIcon} color="gray2.400" />
              </IconButton>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content>
                  <Box px={3} py={2} borderBottomWidth="1px" borderBottomColor="whiteAlpha.200">
                    <Text fontSize="sm" fontWeight="semibold">
                      用紙サイズを選択
                    </Text>
                  </Box>
                  {Object.entries(DOCUMENT_SIZES).map(([key, { width, height }]) => (
                    <Menu.Item
                      key={key}
                      value={key}
                      onClick={() => handleDocumentSizeSelect(key as DocumentSize)}
                      fontSize="sm"
                    >
                      <VStack alignItems="flex-start" gap={0}>
                        <Text fontWeight="semibold">{key}</Text>
                        <Text fontSize="xs" color="gray2.400">
                          {width}×{height}mm
                        </Text>
                      </VStack>
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </HStack>
      )}

      {/* Grid Opacity Section */}
      {!isModifyingMaskRegion && (
        <HStack w="100%" alignItems="center" gap={0}>
          <Text fontSize="sm" fontWeight="bold">
            グリッド透明度
          </Text>
          <Spacer />
          <RangeInput
            key="gridOpacity"
            colorPalette="black"
            testId="gridOpacity"
            min={0}
            max={100}
            step={10}
            precision={0}
            value={gridOpacity * 100}
            defaultValue={GRID_OPACITY * 100}
            displayValue={`${Math.round(gridOpacity * 100)}`}
            unit="%"
            updateValue={handleGridOpacityChange}
          />
        </HStack>
      )}
    </VStack>
  )
}

export default BlueprintSettings
